// Helper functions for chargers

import { sub, differenceInMinutes } from "date-fns";

import { isEmpty } from "./general";

export const CHGR_MODE_OPTIONS = ["OPEN", "AUTHENTICATION"] as const;

const CHGR_MODE_ALIASES: Record<string, (typeof CHGR_MODE_OPTIONS)[number]> = {
  "1": "OPEN",
  OPEN: "OPEN",
  "2": "AUTHENTICATION",
  AUTHENTICATION: "AUTHENTICATION",
};

export function normalizeChargerConfigValue(key: string, value: string): string {
  if (key !== "chgrMode") {
    return value;
  }
  return CHGR_MODE_ALIASES[value] ?? value;
}

// Capacity limiting must store ClmAbsLimit before CurrentLimitMode. Sending the
// mode first can leave the charger in that mode with the previous limit if the
// limit command is then rejected.
export function orderClmAbsLimitBeforeMode(
  entries: Array<[string, string]>
): Array<[string, string]> {
  const limitIndex = entries.findIndex(([key]) => key === "ClmAbsLimit");
  const modeIndex = entries.findIndex(([key]) => key === "CurrentLimitMode");
  if (limitIndex === -1 || modeIndex === -1 || limitIndex < modeIndex) {
    return entries;
  }
  const next = entries.slice();
  const [limit] = next.splice(limitIndex, 1);
  const modeAt = next.findIndex(([key]) => key === "CurrentLimitMode");
  next.splice(modeAt, 0, limit);
  return next;
}

export function normalizeChargerConfigState(
  config: Record<string, string>
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(config).map(([key, value]) => [
      key,
      normalizeChargerConfigValue(key, value),
    ])
  );
}

type Location = {
  street_address: string;
  suburb: string;
  city: string;
  province: string;
  postcode: string;
  country: string;
};

export const constructLocationString = (location: Location) => {
  return (isEmpty(location) || Object.values(location).every((value) => value === ""))
    ? "Unknown location"
    : `${
      ((location?.street_address.length > 0)
        ? location?.street_address + ","
        : "")
      + ((location?.suburb.length > 0)
        ? " " + location?.suburb + ","
        : "")
      + ((location?.city.length > 0)
        ? " " + location?.city + ","
        : "")
      + ((location?.province.length > 0)
        ? " " + location?.province
        : "")
      + ((location?.postcode.length > 0)
        ? " " + location?.postcode + ","
        : "")
      + ((location?.country.length > 0)
        ? " " + location?.country
        : "")}`;
};

export const lastCommsFilterToTimestamp = (filterOption: string) => {
  switch (filterOption) {
  case "< 10 mins":
    return `<${sub(new Date(), { minutes: 10 }).toISOString()}`;
  case "< 1 hour":
    return `<${sub(new Date(), { hours: 1 }).toISOString()}`;
  case "< 6 hours":
    return `<${sub(new Date(), { hours: 6 }).toISOString()}`;
  case "< 24 hours":
    return `<${sub(new Date(), { hours: 24 }).toISOString()}`;
  case "> 24 hours":
    return `>${sub(new Date(), { hours: 24 }).toISOString()}`;
  default:
    return "";
  }
};

export const lastCommsTimestampToFilter = (timestamp: string) => {
  const minutesAgo = differenceInMinutes(new Date(), new Date(timestamp.substring(1)));
  if (minutesAgo <= 10) {
    return "< 10 mins";
  } else if (minutesAgo <= 60) {
    return "< 1 hour";
  } else if (minutesAgo <= 360) {
    return "< 6 hours";
  } else if (minutesAgo <= 1440) {
    return "< 24 hours";
  } else if (minutesAgo >= 1440) {
    return "> 24 hours";
  } else {
    return "";
  }
};